import { format, addDays, subDays, isSameDay } from "date-fns";
import { formatInCompanyTz, isSameCompanyDay, toCompanyDate } from "@/lib/scheduleTime";
import { Calendar, ChevronLeft, ChevronRight, Clock, User, Plus, Palmtree, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { LeaveOverlayBadge } from "./LeaveOverlayBadge";
import { PublicHolidayBadge } from "./PublicHolidayBadge";
import type { LeaveOverlay } from "@/services/leave/leaveOverlayTypes";
import type { PublicHolidayOverlay } from "@/hooks/usePublicHolidayOverlay";

interface Shift {
  id: string;
  profile_id: string | null;
  start_time: string;
  end_time: string;
  note: string | null;
  employee_name: string;
  status?: 'active' | 'open';
  offered_by_user_id?: string | null;
  offered_by_name?: string | null;
  shift_code?: string | null;
  is_all_day?: boolean;
  shift_code_info?: {
    code: string;
    name: string;
    color: string;
    category: string;
  } | null;
}

interface DayViewProps {
  selectedDate: Date;
  onDateChange: (date: Date) => void;
  shifts: Shift[];
  onCreateShift: (date: Date) => void;
  onShiftClick?: (shift: Shift) => void;
  /** Approved leave overlays to display (optional) */
  leaveOverlays?: LeaveOverlay[];
  /** Public holiday overlays (optional) */
  publicHolidays?: PublicHolidayOverlay[];
  /** Company IANA timezone (e.g. "Europe/Berlin"). Defaults to "UTC". */
  companyTimezone?: string;
}

export function DayView({ selectedDate, onDateChange, shifts, onCreateShift, onShiftClick, leaveOverlays = [], publicHolidays = [], companyTimezone = 'UTC' }: DayViewProps) {
  const tz = companyTimezone;
  const dayShifts = shifts.filter(shift => 
    isSameCompanyDay(shift.start_time, selectedDate, tz)
  );
  
  const allDayShifts = dayShifts.filter(shift => shift.is_all_day);
  const timedShifts = dayShifts.filter(shift => !shift.is_all_day);
  
  // Get leave overlays for the selected date
  const dayLeaves = leaveOverlays.filter(leave => {
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    return dateStr >= leave.start_date && dateStr <= leave.end_date;
  });

  // Get public holiday for the selected date
  const dayHoliday = publicHolidays.find(h => h.date === format(selectedDate, 'yyyy-MM-dd'));

  const now = new Date();
  
  // Enhanced status display logic to show offered by information
  const getShiftDisplayInfo = (shift: Shift) => {
    if (shift.status === 'open' || !shift.profile_id) {
      // Check if this is an offered shift
      if (shift.offered_by_user_id && shift.offered_by_name) {
        return {
          label: `Offered by ${shift.offered_by_name}`,
          className: "border-dashed bg-orange-50 text-orange-700 border-orange-300"
        };
      } else {
        return {
          label: "Open shift",
          className: "border-dashed bg-muted/40 text-muted-foreground border-muted-foreground/30"
        };
      }
    } else {
      // Assigned shift - use shift code color
      const color = shift.shift_code_info?.color || '#94a3b8';
      return {
        label: shift.employee_name,
        color: color
      };
    }
  };
  
  const onShiftShifts = timedShifts.filter(shift => {
    const shiftStart = new Date(shift.start_time);
    const shiftEnd = new Date(shift.end_time);
    return now >= shiftStart && now <= shiftEnd;
  });

  const upcomingShifts = timedShifts.filter(shift => {
    return new Date(shift.start_time) > now;
  });

  const pastShifts = timedShifts.filter(shift => {
    return new Date(shift.end_time) < now;
  });

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const renderShiftCard = (shift: Shift, status: 'on-shift' | 'upcoming') => {
    const displayInfo = getShiftDisplayInfo(shift);
    const isOpenShift = shift.status === 'open' || !shift.profile_id;
    
    return (
      <Card 
        key={shift.id} 
        className={`p-4 ${displayInfo.className || ''} ${onShiftClick ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
        style={displayInfo.color ? {
          backgroundColor: `${displayInfo.color}15`,
          borderLeft: `4px solid ${displayInfo.color}`
        } : undefined}
        onClick={() => onShiftClick?.(shift)}
      >
        <div className="flex items-center gap-3">
          {!isOpenShift && (
            <div 
              className="h-10 w-10 rounded-full flex items-center justify-center text-white font-semibold text-sm"
              style={displayInfo.color ? { backgroundColor: displayInfo.color } : { backgroundColor: '#94a3b8' }}
            >
              {getInitials(shift.employee_name)}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="font-medium truncate" style={displayInfo.color ? { color: displayInfo.color } : undefined}>{displayInfo.label}</div>
            <div className="text-sm text-muted-foreground flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {formatInCompanyTz(shift.start_time, tz, 'h:mm a')} - {formatInCompanyTz(shift.end_time, tz, 'h:mm a')}
            </div>
            {shift.note && (
              <div className="text-xs text-muted-foreground mt-1 truncate">
                {shift.note}
              </div>
            )}
          </div>
          <Badge variant={status === 'on-shift' ? 'default' : 'secondary'}>
            {status === 'on-shift' ? 'On Shift' : 'Upcoming'}
          </Badge>
        </div>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      {/* Date Navigation */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              size="icon"
              onClick={() => onDateChange(subDays(selectedDate, 1))}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            
            <div className="text-center">
              <div className="font-semibold">{format(selectedDate, 'EEEE')}</div>
              <div className="text-sm text-muted-foreground">
                {format(selectedDate, 'MMMM d, yyyy')}
              </div>
              {dayHoliday && (
                <div className="flex items-center justify-center gap-1 mt-1 text-xs text-rose-600 dark:text-rose-400 font-medium">
                  <Star className="h-3 w-3 fill-current" />
                  {dayHoliday.name}
                </div>
              )}
            </div>

            <Button
              variant="outline"
              size="icon"
              onClick={() => onDateChange(addDays(selectedDate, 1))}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* Quick Actions */}
      <Card>
        <CardContent className="pt-6">
          <Button 
            onClick={() => onCreateShift(selectedDate)}
            className="w-full"
            size="lg"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create New Shift
          </Button>
        </CardContent>
      </Card>

      {/* Public Holiday Section */}
      {dayHoliday && (
        <div>
          <PublicHolidayBadge holiday={dayHoliday} />
        </div>
      )}

      {/* On Leave Section */}
      {dayLeaves.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Palmtree className="h-5 w-5 text-amber-600" />
            <h3 className="font-semibold text-amber-600">On Leave</h3>
            <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-200">
              {dayLeaves.length}
            </Badge>
          </div>
          <div className="space-y-2">
            {dayLeaves.map(leave => (
              <LeaveOverlayBadge key={leave.leave_request_id} leave={leave} />
            ))}
          </div>
        </div>
      )}

      {/* All-Day Shifts (Days Off) */}
      {allDayShifts.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Calendar className="h-5 w-5" />
            <h3 className="font-semibold">Day Off</h3>
            <Badge variant="outline">
              {allDayShifts.length}
            </Badge>
          </div>
          <div className="space-y-2">
            {allDayShifts.map(shift => {
              const bgColor = shift.shift_code_info?.color || '#94a3b8';
              return (
                <Card 
                  key={shift.id}
                  className={`p-4 ${onShiftClick ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
                  style={{ 
                    backgroundColor: `${bgColor}15`,
                    borderLeft: `4px solid ${bgColor}`
                  }}
                  onClick={() => onShiftClick?.(shift)}
                >
                  <div className="flex items-center gap-3">
                    <div 
                      className="h-10 w-10 rounded-full flex items-center justify-center text-white font-semibold"
                      style={{ backgroundColor: bgColor }}
                    >
                      {shift.employee_name.split(' ').map(n => n[0]).join('').toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium">{shift.employee_name}</div>
                      <div className="text-sm" style={{ color: bgColor }}>
                        {shift.shift_code_info?.name || shift.shift_code} - All day
                      </div>
                      {shift.note && (
                        <div className="text-xs text-muted-foreground mt-1 truncate">
                          {shift.note}
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* On Shift */}
      {onShiftShifts.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <User className="h-5 w-5 text-green-600" />
            <h3 className="font-semibold text-green-600">On Shift Now</h3>
            <Badge variant="default" className="bg-green-100 text-green-800">
              {onShiftShifts.length}
            </Badge>
          </div>
          <div className="space-y-2">
            {onShiftShifts.map(shift => renderShiftCard(shift, 'on-shift'))}
          </div>
        </div>
      )}

      {/* Upcoming Shifts */}
      {upcomingShifts.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Calendar className="h-5 w-5 text-blue-600" />
            <h3 className="font-semibold text-blue-600">Upcoming Shifts</h3>
            <Badge variant="secondary">
              {upcomingShifts.length}
            </Badge>
          </div>
          <div className="space-y-2">
            {upcomingShifts.map(shift => renderShiftCard(shift, 'upcoming'))}
          </div>
        </div>
      )}

      {/* Past Shifts */}
      {pastShifts.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Clock className="h-5 w-5 text-muted-foreground" />
            <h3 className="font-semibold text-muted-foreground">Past Shifts</h3>
            <Badge variant="outline">
              {pastShifts.length}
            </Badge>
          </div>
          <div className="space-y-2">
            {pastShifts.map(shift => {
              const displayInfo = getShiftDisplayInfo(shift);
              const isOpenShift = shift.status === 'open' || !shift.profile_id;
              
              return (
                <Card 
                  key={shift.id} 
                  className={`p-4 opacity-75 ${displayInfo.className || ''} ${onShiftClick ? 'cursor-pointer hover:opacity-60 transition-opacity' : ''}`}
                  style={displayInfo.color ? {
                    backgroundColor: `${displayInfo.color}15`,
                    borderLeft: `4px solid ${displayInfo.color}`
                  } : undefined}
                  onClick={() => onShiftClick?.(shift)}
                >
                  <div className="flex items-center gap-3">
                    {!isOpenShift && (
                      <div 
                        className="h-10 w-10 rounded-full flex items-center justify-center text-white font-semibold text-sm"
                        style={displayInfo.color ? { backgroundColor: displayInfo.color } : { backgroundColor: '#94a3b8' }}
                      >
                        {getInitials(shift.employee_name)}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate" style={displayInfo.color ? { color: displayInfo.color } : undefined}>{displayInfo.label}</div>
                      <div className="text-sm text-muted-foreground flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatInCompanyTz(shift.start_time, tz, 'h:mm a')} - {formatInCompanyTz(shift.end_time, tz, 'h:mm a')}
                      </div>
                      {shift.note && (
                        <div className="text-xs text-muted-foreground mt-1 truncate">
                          {shift.note}
                        </div>
                      )}
                    </div>
                    <Badge variant="outline" className="text-muted-foreground">
                      Completed
                    </Badge>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* No Shifts */}
      {dayShifts.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-medium mb-2">No shifts scheduled</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Create a new shift for {format(selectedDate, 'EEEE, MMMM d')}
            </p>
            <Button onClick={() => onCreateShift(selectedDate)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Shift
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
